export async function fetchAndSumBalanceHistory(addresses) {
  const DAYS = 365;
  const settledHistories = await Promise.allSettled(addresses.map(async (address) => {
    const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
      || window.location.hostname.endsWith('.local');
    const url = isLocal
      ? `/api-local/kaspa-balance-history?address=${encodeURIComponent(address)}`
      : `/.netlify/functions/kaspa-balance-history?address=${encodeURIComponent(address)}`;
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) throw new Error(`Failed to fetch balance history for ${address}: HTTP ${response.status}`);

    const data = await response.json();
    const balanceDataset = (data.datasets || []).find(dataset => dataset.label === 'Balance');
    if (!balanceDataset || !Array.isArray(data.labels) || !Array.isArray(balanceDataset.data)) {
      throw new Error(`Invalid balance history response for ${address}`);
    }

    const dailyBalances = new Map();
    data.labels.forEach((label, index) => {
      const day = new Date(label).toISOString().slice(0, 10);
      dailyBalances.set(day, Number(balanceDataset.data[index]) || 0);
    });
    return dailyBalances;
  }));
  const addressHistories = settledHistories
    .filter(result => result.status === 'fulfilled')
    .map(result => result.value);
  const failedCount = settledHistories.length - addressHistories.length;
  if (addressHistories.length === 0) {
    const firstFailure = settledHistories.find(result => result.status === 'rejected')?.reason;
    throw new Error(`Unable to load mining wallet history for any of the ${addresses.length} addresses${firstFailure?.message ? `: ${firstFailure.message}` : ''}`);
  }

  const days = [];
  for (let index = DAYS - 1; index >= 0; index -= 1) {
    const date = new Date();
    date.setDate(date.getDate() - index);
    days.push(date.toISOString().slice(0, 10));
  }

  const result = days.map((day) => ({
    date: day,
    balance: addressHistories.reduce((total, history) => {
      let latestBalance = 0;
      for (const [historyDay, balance] of history) {
        if (historyDay <= day) latestBalance = balance;
        else break;
      }
      return total + latestBalance;
    }, 0),
  }));

  console.log('Kaspalytics combined mining wallet balance history:', result);
  return { history: result, failedCount, totalAddresses: settledHistories.length };
}

export async function fetchKasAddedHistory(addresses, days = 14) {
  const addressSet = new Set(addresses.map(address => address.toLowerCase()));
  const dayMilliseconds = 24 * 60 * 60 * 1000;
  const todayStart = new Date();
  todayStart.setUTCHours(0, 0, 0, 0);
  const chartStart = new Date(todayStart);
  chartStart.setUTCDate(chartStart.getUTCDate() - (days - 1));
  const dateKeys = Array.from({ length: days }, (_, index) => {
    const date = new Date(chartStart);
    date.setUTCDate(chartStart.getUTCDate() + index);
    return date.toISOString().slice(0, 10);
  });
  const todayKey = todayStart.toISOString().slice(0, 10);
  const cacheKey = `kas-added-history-v1:${addresses.map(address => address.toLowerCase()).sort().join('|')}`;
  let cachedTotals = {};
  try {
    const saved = JSON.parse(localStorage.getItem(cacheKey) || 'null');
    if (saved?.version === 1 && saved.dailyTotals && typeof saved.dailyTotals === 'object') {
      cachedTotals = saved.dailyTotals;
    }
  } catch {
    // Ignore unavailable or malformed local cache and rebuild it from the API.
  }

  // Closed days are immutable for this chart. Always refresh today, but reuse
  // earlier daily totals already fetched on this browser.
  const firstUncachedKey = dateKeys.find(date => date === todayKey || !Object.prototype.hasOwnProperty.call(cachedTotals, date));
  const fetchStartKey = firstUncachedKey || todayKey;
  const fetchStartDate = new Date(`${fetchStartKey}T00:00:00.000Z`);
  const windowStart = fetchStartDate.getTime();
  const windowEnd = todayStart.getTime() + dayMilliseconds;
  const after = windowStart - 1;
  const pageDiagnostics = [];

  const settledTransactions = await Promise.allSettled(addresses.map(async (address) => {
    const transactions = [];
    const seenCursors = new Set();
    let afterCursor = after;

    while (true) {
      const params = new URLSearchParams({
        limit: '500',
        after: String(afterCursor),
        resolve_previous_outpoints: 'light',
        acceptance: 'accepted',
      });

      const response = await fetch(
        `https://api.kaspa.org/addresses/${encodeURIComponent(address)}/full-transactions-page?${params}`,
        { cache: 'no-store' }
      );
      const responseBody = await response.text();
      if (!response.ok) {
        throw new Error(`Kaspa transaction lookup failed for ${address}: HTTP ${response.status}${responseBody ? ` - ${responseBody.slice(0, 300)}` : ''}`);
      }

      let page;
      try {
        page = JSON.parse(responseBody);
      } catch {
        throw new Error(`Invalid transaction JSON response for ${address}`);
      }
      if (!Array.isArray(page)) throw new Error(`Invalid transaction response for ${address}`);
      transactions.push(...page);

      const nextAfter = response.headers.get('x-next-page-after');
      const newestTime = page.reduce((newest, transaction) => Math.max(newest, Number(transaction.block_time) || 0), 0);
      pageDiagnostics.push({
        cursor: `after=${afterCursor}`,
        transactionCount: page.length,
        newestBlockTime: newestTime || null,
        oldestBlockTime: page.reduce((oldest, transaction) => Math.min(oldest, Number(transaction.block_time) || Infinity), Infinity) || null,
        nextAfter,
      });
      if (!nextAfter || seenCursors.has(nextAfter) || newestTime >= windowEnd || Number(nextAfter) <= afterCursor) break;
      seenCursors.add(nextAfter);
      afterCursor = Number(nextAfter);
    }

    return transactions;
  }));

  const transactionResults = settledTransactions
    .filter(result => result.status === 'fulfilled')
    .flatMap(result => result.value);
  const failedCount = settledTransactions.filter(result => result.status === 'rejected').length;
  if (transactionResults.length === 0 && failedCount === addresses.length) {
    const firstFailure = settledTransactions.find(result => result.status === 'rejected')?.reason;
    throw new Error(`Unable to load confirmed mining wallet transactions${firstFailure?.message ? `: ${firstFailure.message}` : ''}`);
  }

  const dailyTotals = new Map();
  const seenTransactions = new Set();
  const aggregationDiagnostics = {
    uniqueTransactions: 0,
    duplicateTransactions: 0,
    unacceptedTransactions: 0,
    outOfWindowTransactions: 0,
    internalTransfers: 0,
    transactionsWithoutIncomingOutputs: 0,
    countedTransactions: 0,
  };
  transactionResults.forEach(transaction => {
    if (!transaction.is_accepted || !transaction.transaction_id) {
      aggregationDiagnostics.unacceptedTransactions += 1;
      return;
    }
    if (seenTransactions.has(transaction.transaction_id)) {
      aggregationDiagnostics.duplicateTransactions += 1;
      return;
    }
    seenTransactions.add(transaction.transaction_id);
    aggregationDiagnostics.uniqueTransactions += 1;

    const blockTime = Number(transaction.block_time);
    if (!Number.isFinite(blockTime) || blockTime < windowStart || blockTime >= windowEnd) {
      aggregationDiagnostics.outOfWindowTransactions += 1;
      return;
    }

    // Do not count transfers between addresses in this mining wallet as new deposits.
    const isInternalTransfer = (transaction.inputs || []).some(input =>
      addressSet.has((input.previous_outpoint_address || '').toLowerCase())
    );
    if (isInternalTransfer) {
      aggregationDiagnostics.internalTransfers += 1;
      return;
    }

    const date = new Date(blockTime).toISOString().slice(0, 10);
    const receivedSompi = (transaction.outputs || []).reduce((sum, output) => (
      addressSet.has((output.script_public_key_address || '').toLowerCase())
        ? sum + Number(output.amount || 0)
        : sum
    ), 0);
    if (receivedSompi > 0) {
      aggregationDiagnostics.countedTransactions += 1;
      dailyTotals.set(date, (dailyTotals.get(date) || 0) + receivedSompi / 1e8);
    } else {
      aggregationDiagnostics.transactionsWithoutIncomingOutputs += 1;
    }
  });

  const history = Array.from({ length: days }, (_, index) => {
    const date = dateKeys[index];
    const added = date >= fetchStartKey
      ? dailyTotals.get(date) || 0
      : Number(cachedTotals[date]) || 0;
    return { date, added };
  });

  if (failedCount === 0) {
    const totalsToCache = {};
    history.forEach(({ date, added }) => { totalsToCache[date] = added; });
    try {
      localStorage.setItem(cacheKey, JSON.stringify({ version: 1, dailyTotals: totalsToCache }));
    } catch {
      // Keep the chart usable if browser storage is disabled or full.
    }
  }

  console.log('[DEBUG] KAS added confirmed-transaction aggregation', {
    address: addresses[0],
    windowStart: new Date(windowStart).toISOString(),
    windowEndExclusive: new Date(windowEnd).toISOString(),
    cachedCompletedDays: dateKeys.filter(date => date < fetchStartKey),
    fetchedFromDate: fetchStartKey,
    pages: pageDiagnostics,
    rawTransactionRecords: transactionResults.length,
    failedAddressRequests: failedCount,
    ...aggregationDiagnostics,
    dailyTotals: history,
  });

  return { history, failedCount, totalAddresses: addresses.length };
}

// Fetch USDC balance history from Etherscan
export async function fetchUsdcBalanceHistory(walletAddress, etherscanApiKey) {
  const DAYS = 30;
  const USDC_CONTRACT = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';
  const DECIMALS = 6;

  try {
    const url = `https://api.etherscan.io/v2/api?chainid=1&module=account&action=tokentx&contractaddress=${USDC_CONTRACT}&address=${walletAddress}&startblock=0&endblock=99999999&sort=asc&apikey=${etherscanApiKey}`;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const data = await response.json();
    if (data.status === '0' || !Array.isArray(data.result)) {
      return Array.from({ length: DAYS }, (_, index) => {
        const date = new Date();
        date.setDate(date.getDate() - (DAYS - 1 - index));
        return { date: date.toISOString().slice(0, 10), usdcBalance: 0 };
      });
    }

    const dailyChanges = new Map();
    data.result.forEach((tx) => {
      const day = new Date(parseInt(tx.timeStamp, 10) * 1000).toISOString().slice(0, 10);
      const value = parseInt(tx.value, 10) / (10 ** DECIMALS);
      const amount = tx.to.toLowerCase() === walletAddress.toLowerCase() ? value : -value;
      dailyChanges.set(day, (dailyChanges.get(day) || 0) + amount);
    });

    let cumulativeBalance = 0;
    dailyChanges.forEach((value, day) => {
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - (DAYS - 1));
      if (new Date(day) < cutoff) cumulativeBalance += value;
    });

    return Array.from({ length: DAYS }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (DAYS - 1 - index));
      const day = date.toISOString().slice(0, 10);
      cumulativeBalance += dailyChanges.get(day) || 0;
      return { date: day, usdcBalance: cumulativeBalance };
    });
  } catch (error) {
    console.error('Error fetching USDC balance history:', error);
    return Array.from({ length: DAYS }, (_, index) => {
      const date = new Date();
      date.setDate(date.getDate() - (DAYS - 1 - index));
      return { date: date.toISOString().slice(0, 10), usdcBalance: 0 };
    });
  }
}
